import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import FarmlessIcon from "@/components/icons/farmless-icon";
import type { BrewSensorySessionId } from "@brewbase/db/types";

export const Route = createFileRoute("/sensory/join/brew/$sensory_session_id")({
  component: BrewPublicJoinPage,
  parseParams: (params) => ({
    sensory_session_id: parseInt(
      params.sensory_session_id,
      10,
    ) as unknown as BrewSensorySessionId,
  }),
});

function BrewPublicJoinPage() {
  const { sensory_session_id } = Route.useParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: sessionInfo, isLoading: sessionLoading } =
    trpc.brewSensory.public.getSessionInfo.useQuery(sensory_session_id, {
      retry: false,
    });

  const joinMutation = trpc.brewSensory.public.joinByEmail.useMutation({
    onSuccess: (result) => {
      if (result.found && result.token) {
        navigate({ to: "/sensory/$token", params: { token: result.token } });
      } else {
        setNeedsName(true);
      }
    },
    onError: (err) => setErrorMsg(err.message),
  });

  const guestMutation = trpc.brewSensory.public.joinAsGuest.useMutation({
    onSuccess: (result) => {
      navigate({ to: "/sensory/$token", params: { token: result.token } });
    },
    onError: (err) => setErrorMsg(err.message),
  });

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!email.trim()) return;
    joinMutation.mutate({
      brew_sensory_session_id: sensory_session_id,
      email: email.trim().toLowerCase(),
    });
  };

  const handleGuestSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    if (!name.trim()) return;
    guestMutation.mutate({
      brew_sensory_session_id: sensory_session_id,
      email: email.trim().toLowerCase(),
      name: name.trim(),
    });
  };

  if (sessionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isPending = joinMutation.isPending || guestMutation.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-sm w-full space-y-6">
        <div className="text-center space-y-2">
          <FarmlessIcon className="h-10 w-10 mx-auto text-foreground/70" />
          <h1 className="text-lg font-semibold">
            {sessionInfo?.name || "Sensory Evaluation"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {needsName
              ? "We don't recognize that email. Please enter your name to continue."
              : "Enter your email to start the evaluation."}
          </p>
        </div>

        {!needsName && (
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorMsg(null);
                }}
                autoFocus
              />
              {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={!email.trim() || isPending}
            >
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Continue
            </Button>
          </form>
        )}

        {needsName && (
          <form onSubmit={handleGuestSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="guest-email">Email</Label>
              <Input
                id="guest-email"
                type="email"
                value={email}
                disabled
                className="opacity-60"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrorMsg(null);
                }}
                autoFocus
              />
              {errorMsg && <p className="text-xs text-destructive">{errorMsg}</p>}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setNeedsName(false);
                  setName("");
                  setErrorMsg(null);
                }}
                disabled={isPending}
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={!name.trim() || isPending}
              >
                {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Join Evaluation
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
